import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router';
import { api } from '../api.ts';
import { authClient } from '../auth-client.ts';
import { ErrorText } from '../components/ErrorText.tsx';
import { PasswordFields } from '../components/PasswordFields.tsx';
import { setLang } from '../i18n/index.ts';

function Narrow({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <main className="narrow" id="main">
      <div className="card">
        <h2>{title}</h2>
        {children}
      </div>
    </main>
  );
}

export function ForgotPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    await authClient.requestPasswordReset({ email: email.trim(), redirectTo: '/reset-password' });
    setSent(true);
  };
  return (
    <Narrow title={t('forgot.title')}>
      {sent ? (
        <p role="status">{t('forgot.sent')}</p>
      ) : (
        <form onSubmit={submit}>
          <p className="k">{t('forgot.intro')}</p>
          <label>
            {t('login.email')}
            <input
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <button className="btn">{t('forgot.submit')}</button>
        </form>
      )}
      <p>
        <Link to="/login">{t('common.back')}</Link>
      </p>
    </Narrow>
  );
}

export function ResetPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [pw, setPw] = useState(['', '']);
  const [status, setStatus] = useState<'idle' | 'done' | 'invalid' | 'weak' | 'mismatch'>('idle');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (pw[0] !== pw[1]) return setStatus('mismatch');
    const r = await authClient.resetPassword({ newPassword: pw[0]!, token });
    if (!r.error) return setStatus('done');
    setStatus(r.error.status === 400 && /weak/i.test(r.error.message ?? '') ? 'weak' : 'invalid');
  };
  if (status === 'done')
    return (
      <Narrow title={t('reset.title')}>
        <p role="status">{t('reset.done')}</p>
        <Link className="btn" to="/login">
          {t('login.submit')}
        </Link>
      </Narrow>
    );
  return (
    <Narrow title={t('reset.title')}>
      <form onSubmit={submit}>
        <PasswordFields password={pw[0]!} repeat={pw[1]!} onChange={(a, b) => setPw([a, b])} />
        {status === 'mismatch' && (
          <p className="err" role="alert">
            {t('password.mismatch')}
          </p>
        )}
        {status === 'weak' && (
          <p className="err" role="alert">
            {t('errors.weak_password')}
          </p>
        )}
        {status === 'invalid' && (
          <p className="err" role="alert">
            {t('reset.invalid')} <Link to="/forgot-password">{t('login.forgot')}</Link>
          </p>
        )}
        <button className="btn">{t('reset.submit')}</button>
      </form>
    </Narrow>
  );
}

export function InvitePage() {
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const info = useQuery({
    queryKey: ['invite', token],
    queryFn: () =>
      api.get<{ email: string; firstName: string; language: 'sv' | 'en' }>(
        `/api/account/invite/${token}`,
      ),
    retry: false,
  });
  const [pw, setPw] = useState(['', '']);
  const [error, setError] = useState<unknown>(null);
  const [mismatch, setMismatch] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (info.data?.language) setLang(info.data.language);
  }, [info.data?.language]);

  if (info.isLoading) return <Narrow title="…">{t('common.loading')}</Narrow>;
  if (info.isError)
    return (
      <Narrow title={t('confirmEmail.title')}>
        <p className="err">{t('invite.invalid')}</p>
      </Narrow>
    );
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setMismatch(pw[0] !== pw[1]);
    if (pw[0] !== pw[1]) return;
    try {
      await api.post(`/api/account/invite/${token}`, { password: pw[0] });
      await authClient.signIn.email({ email: info.data!.email, password: pw[0]! });
      setDone(true);
    } catch (err) {
      setError(err);
    }
  };
  if (done)
    return (
      <Narrow title={t('invite.title', { name: info.data!.firstName })}>
        <p role="status">{t('invite.done')}</p>
        <a className="btn" href="/">
          {t('twofa.continue')}
        </a>
      </Narrow>
    );
  return (
    <Narrow title={t('invite.title', { name: info.data!.firstName })}>
      <form onSubmit={submit}>
        <p>{t('invite.intro', { email: info.data!.email })}</p>
        <input type="email" autoComplete="username" value={info.data!.email} readOnly hidden />
        <PasswordFields password={pw[0]!} repeat={pw[1]!} onChange={(a, b) => setPw([a, b])} />
        {mismatch && (
          <p className="err" role="alert">
            {t('password.mismatch')}
          </p>
        )}
        <ErrorText error={error} />
        <button className="btn">{t('invite.submit')}</button>
      </form>
    </Narrow>
  );
}

export function ConfirmEmailPage() {
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const [state, setState] = useState<{ email?: string; error?: boolean }>({});
  return (
    <Narrow title={t('confirmEmail.title')}>
      {state.email ? (
        <p role="status">{t('confirmEmail.done', { email: state.email })}</p>
      ) : state.error ? (
        <p className="err">{t('confirmEmail.invalid')}</p>
      ) : (
        <button
          className="btn"
          onClick={() =>
            api
              .post<{ email: string }>(`/api/account/confirm-email/${token}`)
              .then((r) => setState({ email: r.email }))
              .catch(() => setState({ error: true }))
          }
        >
          {t('common.confirm')}
        </button>
      )}
      <p>
        <Link to="/">{t('common.back')}</Link>
      </p>
    </Narrow>
  );
}
